import { useQueryClient } from '@tanstack/react-query'
import { listingImagesApi } from '~/entities/listing/api'
import { AiImageButton } from '~/features/ai-studio/components'
import { CoverAdjustButton } from '~/features/shared/components/cover-adjust'
import {
  type ExistingImage,
  type ImageFile,
  ImagesSection,
} from '~/features/shared/components/images-section'
import * as m from '~/paraglide/messages'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'

interface ListingImagesSectionProps {
  listingId?: string
  existingImages?: ExistingImage[]
  onImagesChange?: (files: ImageFile[], primaryIndex: number) => void
  onRemovedExistingChange?: (imageIds: string[]) => void
}

export function ListingImagesSection({
  listingId,
  existingImages = [],
  onImagesChange,
  onRemovedExistingChange,
}: ListingImagesSectionProps) {
  const queryClient = useQueryClient()

  const handleDeleteExisting = async (imageId: string) => {
    if (!listingId) throw new Error('Listing ID is required')
    await listingImagesApi.delete(imageId)
    queryClient.invalidateQueries({
      queryKey: queryKey(QueryKey.LISTING, listingId),
    })
  }

  const handleReorderExisting = async (imageIds: string[]) => {
    if (!listingId) throw new Error('Listing ID is required')
    await listingImagesApi.reorder(listingId, imageIds)
    queryClient.invalidateQueries({
      queryKey: queryKey(QueryKey.LISTING, listingId),
    })
  }

  return (
    <ImagesSection
      title={m.listingImages()}
      description={m.uploadImagesForYourListingDragToReorderOrClickToSetAsPrimary()}
      entityId={listingId}
      existingImages={existingImages}
      onImagesChange={onImagesChange}
      onRemovedExistingChange={onRemovedExistingChange}
      onDeleteExisting={handleDeleteExisting}
      onReorderExisting={handleReorderExisting}
      actions={(addImage, cover) => (
        <>
          <AiImageButton onUse={addImage} orientation="landscape" variant="outline" />
          <CoverAdjustButton cover={cover} />
        </>
      )}
    />
  )
}
